import React, { useEffect, useState } from "react";
import { api, formatApiError, formatRupiah } from "@/lib/api";
import { PageHeader, Section, DataCard, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calculator, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function RekapAbsensi() {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance/recap?period=${period}`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const runCompute = async () => {
    try {
      const { data } = await api.post("/payroll/compute", { period });
      toast.success(`Payroll dihitung untuk ${data.computed} karyawan`);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="Rekap Absensi"
        subtitle="Rekap detail hadir, telat, dan lembur per karyawan per periode"
        testId="rekap-header"
        actions={
          <>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="rekap-period" className="w-40 rounded-none bg-neutral-900 border-neutral-800 h-11" />
            <Button onClick={load} variant="outline" className="rounded-none border-neutral-800 h-11"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
            <Button onClick={runCompute} data-testid="compute-payroll" className="rounded-none bg-[#FFD700] hover:bg-[#e6c200] text-black font-bold"><Calculator size={14} className="mr-1" /> Hitung Payroll</Button>
          </>
        }
      />
      <Section>
        {loading ? <div className="text-sm text-neutral-500 font-mono">Memuat...</div>
        : !data || data.recap.length === 0 ? <Empty message="Tidak ada absensi periode ini" />
        : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <DataCard label="Karyawan Absen" value={data.recap.length} accent="orange" />
              <DataCard label="Total Hari Hadir" value={data.recap.reduce((s, r) => s + r.total_hadir, 0)} />
              <DataCard label="Total Terlambat" value={data.recap.reduce((s, r) => s + r.days_late, 0)} accent="red" />
              <DataCard label="Total Jam Lembur" value={data.recap.reduce((s, r) => s + r.total_lembur_hours, 0).toFixed(1)} accent="yellow" />
            </div>
            <div className="border border-neutral-800 bg-[#141414]">
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Karyawan</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Hari Hadir</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Telat (hari)</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Total Menit Telat</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Jam Lembur</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Est. Potongan Telat</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recap.map((r) => (
                    <TableRow key={r.user_id} className="border-neutral-800" data-testid={`rekap-row-${r.user_id}`}>
                      <TableCell className="font-mono">{r.user_name}</TableCell>
                      <TableCell className="font-mono font-bold">{r.total_hadir}</TableCell>
                      <TableCell className="font-mono text-[#DC2626]">{r.days_late}</TableCell>
                      <TableCell className="font-mono">{r.total_telat}</TableCell>
                      <TableCell className="font-mono text-[#FFD700]">{r.total_lembur_hours.toFixed(1)}</TableCell>
                      <TableCell className="font-mono">{formatRupiah(r.total_telat * (data.settings?.late_deduction_per_minute || 0))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
